import {
  formatUserMessageText,
  resolveUserMessage,
  UserMessage
} from 'src/entities/order/lib/order-user-messages';

export { formatUserMessageText, resolveUserMessage };
export type { UserMessage };

export const PURCHASE_NOTE_ERROR_MESSAGES: Record<string, UserMessage> = {
  CREATE_PURCHASE_NOTE_JOB_NOT_FOUND: {
    title: 'Trabajo no encontrado',
    body: 'No se pudo crear las órdenes de pedido. Reintenta o contacta a Soporte TI.',
    action: 'retry'
  },
  CREATE_PURCHASE_NOTE_JOB_ID_REQUIRED: {
    title: 'Identificador inválido',
    body: 'No se pudo crear las órdenes de pedido. Reintenta o contacta a Soporte TI.',
    action: 'retry'
  },
  CREATE_PURCHASE_NOTE_JOB_FAILED: {
    title: 'Error al crear órdenes de pedido',
    body: 'No se pudo crear las órdenes de pedido. Reintenta o contacta a Soporte TI.',
    action: 'retry'
  },
  TIMEOUT_CREATE_PURCHASE_NOTE_JOB: {
    title: 'Tiempo de espera agotado',
    body: 'La creación de órdenes de pedido está tardando más de lo esperado. Reintenta o contacta a Soporte TI.',
    action: 'retry'
  },
  CREATE_PURCHASE_NOTE_ITEMS_REQUIRED: {
    title: 'Sin servicios seleccionados',
    body: 'Debes seleccionar al menos un servicio para crear la orden de pedido.'
  },
  PDF_GENERATION_FAILED: {
    title: 'Error al generar PDF',
    body: 'No se pudo generar el documento PDF de la orden de pedido.',
    contact: 'Reintenta o contacta a Soporte TI.',
    action: 'retry'
  },
  PDF_RETRY_NOT_ALLOWED: {
    title: 'No se puede reintentar',
    body: 'Solo se puede reintentar la generación del PDF cuando falló.',
    action: 'refresh'
  },
  PURCHASE_NOTE_NOT_FOUND: {
    title: 'Orden de pedido no encontrada',
    body: 'No se encontró la orden de pedido solicitada.',
    contact: 'Actualiza la grilla o contacta a Operaciones.',
    action: 'refresh'
  },
  PURCHASE_NOTE_ALREADY_DELETED: {
    title: 'Orden de pedido eliminada',
    body: 'Esta orden de pedido ya fue eliminada.',
    contact: 'Actualiza la grilla.',
    action: 'refresh'
  },
  PURCHASE_NOTE_NOT_DELETABLE: {
    title: 'No se puede eliminar',
    body: 'Solo se pueden eliminar órdenes de pedido en estado CONFIRMADO.',
    action: 'refresh'
  }
};

export const CREATE_PURCHASE_NOTE_REJECTION_MESSAGES: Record<
  string,
  UserMessage
> = {
  DUPLICATED_INPUT: {
    title: 'Servicio duplicado',
    body: 'Servicio duplicado en la selección'
  },
  SERVICE_NOT_FOUND: {
    title: 'Servicio no encontrado',
    body: 'Servicio no encontrado'
  },
  SERVICE_DELETED: {
    title: 'Servicio eliminado',
    body: 'Servicio eliminado'
  },
  SERVICE_ALREADY_HAS_PURCHASE_NOTE: {
    title: 'Ya tiene orden de pedido',
    body: 'Ya tiene orden de pedido'
  },
  MISSING_AMOUNT: {
    title: 'Falta monto',
    body: 'Falta monto'
  }
};

export const CREATE_PURCHASE_NOTE_WARNING_MESSAGES: Record<
  string,
  UserMessage
> = {
  MISSING_PAYMENT_TERMS: {
    title: 'Sin plazo de pago',
    body: 'No se encontraron condiciones de pago; se usó la fecha de hoy.'
  },
  LEGACY_DATA_INCOMPLETE: {
    title: 'Datos incompletos',
    body: 'Algunos datos del servicio o itinerario son incompletos.',
    contact: 'La OP se creó, pero revisa con Operaciones.'
  }
};

export function resolveCreatePurchaseNoteWarningMessage(warning: {
  reason?: string | null;
  message?: string | null;
}): UserMessage {
  const reason = warning?.reason?.trim();
  if (reason && CREATE_PURCHASE_NOTE_WARNING_MESSAGES[reason]) {
    return CREATE_PURCHASE_NOTE_WARNING_MESSAGES[reason];
  }
  return resolveUserMessage(reason, [CREATE_PURCHASE_NOTE_WARNING_MESSAGES]);
}

export function resolveCreatePurchaseNoteRejectionMessage(rejected: {
  reason?: string | null;
  message?: string | null;
}): UserMessage {
  const reason = rejected?.reason?.trim();
  if (reason && CREATE_PURCHASE_NOTE_REJECTION_MESSAGES[reason]) {
    return CREATE_PURCHASE_NOTE_REJECTION_MESSAGES[reason];
  }
  return resolveUserMessage(reason, [CREATE_PURCHASE_NOTE_REJECTION_MESSAGES]);
}

export function resolvePurchaseNoteGraphqlUserMessage(
  error: unknown,
  fallback = 'No se pudo crear las órdenes de pedido. Reintenta o contacta a Soporte TI.'
): UserMessage {
  const err = error as {
    graphQLErrors?: Array<{ extensions?: { code?: string } }>;
  };
  const code = err?.graphQLErrors?.[0]?.extensions?.code ?? null;
  return resolveUserMessage(code, [PURCHASE_NOTE_ERROR_MESSAGES], fallback);
}

export function shouldRefreshAfterPurchaseNoteError(error: unknown): boolean {
  return resolvePurchaseNoteGraphqlUserMessage(error).action === 'refresh';
}
